import React, { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';
import { formatDate } from '../../utils/formatDate';

const LocationTab = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Data for Add/Edit modal
  const [deleteId, setDeleteId] = useState(null); // ID of location to delete
  const [editMode, setEditMode] = useState(false); // Edit mode toggle
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch all locations
  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/locations`);
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Save or update a location
  const handleSave = async (location) => {
    const method = editMode ? 'PUT' : 'POST';
    const url = editMode
      ? `${apiUrl}/api/locations/${location.id}`
      : `${apiUrl}/api/locations`;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...location,
          updated_at: new Date(),
        }),
      });
      if (!response.ok) throw new Error('Error saving location');
      await fetchLocations();
      setModalData(null); // Close modal
    } catch (err) {
      setError('Error saving location. Please try again.');
    }
  };

  // Delete a location
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/locations/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error deleting location');
      await fetchLocations();
      setDeleteId(null); // Close delete confirmation modal
    } catch (err) {
      setError('Error deleting location. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Locations</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: '', code: '' }); // Open modal with empty data
          }}
        >
          Add Location
        </button>
      </div>

      {loading ? (
        <p>Loading locations...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : locations.length === 0 ? (
        <p>No locations available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Code</th>
              <th className="border border-gray-300 bg-base-200">Created</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc) => (
              <tr key={loc.id}>
                <td>{loc.name}</td>
                <td>{loc.code}</td>
                <td>{formatDate(loc.created_at)}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(loc); // Open modal with location data
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(loc.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? 'Edit Location' : 'Add Location'}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="form-control">
              <label htmlFor="code" className="label-text">
                Code
              </label>
              <input
                type="text"
                name="code"
                value={modalData.code}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, code: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)} // Close modal
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>Are you sure you want to delete this location? This action cannot be undone.</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)} // Close modal
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default LocationTab;
